import { FullTheme, resolveTheme, TenantThemeColors } from '../utils/theming';

export type TenantPaymentMethod = 'prepaid' | 'postpaid';

const TENANT_PAYMENT_METHOD_COOKIE = 'tenant-payment-method';
const TENANT_CURRENCY_COOKIE = 'tenant-currency';

export const setItem = (key: string, value: string) => {
  localStorage.setItem(key, value);
};

export const getItem = (key: string) => {
  return localStorage.getItem(key);
};

export const removeItem = (key: string) => {
  localStorage.removeItem(key);
};

export const setCookie = (name: string, value: string, days: number) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/`;
};

export const getCookie = (name: string): string | null => {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  for (const cookie of ca) {
    let c = cookie;
    while (c.startsWith(' ')) c = c.substring(1, c.length);
    if (c.startsWith(nameEQ))
      return decodeURIComponent(c.substring(nameEQ.length, c.length));
  }
  return null;
};

export const deleteCookie = (name: string) => {
  document.cookie = `${name}=; Max-Age=-99999999; path=/`;
};

type TenantThemeCookie = {
  primaryColor: string | null;
  secondaryColor: string | null;
  logoUrl: string | null;
  faviconUrl: string | null;
  themeConfig: Record<string, any> | null;
  metadata?: {
    title?: string | null;
    description?: string | null;
  } | null;
  name?: string | null;
  title?: string | null;
  description?: string | null;
  paymentMethod?: TenantPaymentMethod | null;
  currency?: string | null;
};

type ThemeCookiePayload = {
  colors: TenantThemeCookie;
  computed: FullTheme;
};

const isObject = (value: unknown): value is Record<string, any> =>
  typeof value === 'object' && value !== null;

const isFullTheme = (value: unknown): value is FullTheme =>
  isObject(value) &&
  typeof value.primary === 'string' &&
  typeof value.secondary === 'string' &&
  typeof value.accent === 'string' &&
  typeof value.primaryForeground === 'string' &&
  isObject(value.primaryScale) &&
  isObject(value.secondaryScale);

const isTenantThemeCookie = (value: unknown): value is TenantThemeCookie =>
  isObject(value) && 'primaryColor' in value && 'secondaryColor' in value;

const isTenantPaymentMethod = (value: unknown): value is TenantPaymentMethod =>
  value === 'prepaid' || value === 'postpaid';

const isTenantCurrency = (value: unknown): value is string =>
  typeof value === 'string' && /^[A-Z]{3}$/.test(value);

const flattenTenantMetadata = (
  colors: TenantThemeCookie
): TenantThemeCookie => ({
  ...colors,
  title: colors.metadata?.title ?? colors.title ?? null,
  description: colors.metadata?.description ?? colors.description ?? null,
});

const normalizeThemeCookie = (value: unknown): ThemeCookiePayload | null => {
  if (!isObject(value)) return null;

  if (isTenantThemeCookie(value) && !('computed' in value)) {
    const colors = flattenTenantMetadata(value);
    return {
      colors,
      computed: resolveTheme(colors),
    };
  }

  if ('colors' in value && 'computed' in value) {
    const colors = (value as { colors: unknown }).colors;
    const computed = (value as { computed: unknown }).computed;

    if (isTenantThemeCookie(colors) && isFullTheme(computed)) {
      return { colors: flattenTenantMetadata(colors), computed };
    }

    if (isTenantThemeCookie(colors)) {
      const normalizedColors = flattenTenantMetadata(colors);
      return {
        colors: normalizedColors,
        computed: resolveTheme(normalizedColors),
      };
    }
  }

  return null;
};

export const setThemeCookie = (theme: TenantThemeColors) => {
  const normalizedTheme = {
    ...theme,
    title: theme.metadata?.title ?? theme.title ?? null,
    description: theme.metadata?.description ?? theme.description ?? null,
  };
  const payload: ThemeCookiePayload = {
    colors: normalizedTheme as TenantThemeCookie,
    computed: resolveTheme(normalizedTheme),
  };
  setCookie('tenant-theme', JSON.stringify(payload), 365);
  deleteCookie(TENANT_PAYMENT_METHOD_COOKIE);

  if (isTenantCurrency(normalizedTheme.currency)) {
    setTenantCurrencyCookie(normalizedTheme.currency);
  } else {
    deleteCookie(TENANT_CURRENCY_COOKIE);
  }
};

export const getThemeCookie = () => {
  const theme = getCookie('tenant-theme');
  if (!theme) return null;

  try {
    const parsedTheme = JSON.parse(theme);
    const normalizedTheme = normalizeThemeCookie(parsedTheme);

    return normalizedTheme;
  } catch {
    return null;
  }
};

export const setTenantPaymentMethodCookie = (
  paymentMethod: TenantPaymentMethod
) => {
  const cachedTheme = getThemeCookie();
  const colors: TenantThemeCookie = {
    primaryColor: cachedTheme?.colors.primaryColor ?? null,
    secondaryColor: cachedTheme?.colors.secondaryColor ?? null,
    logoUrl: cachedTheme?.colors.logoUrl ?? null,
    faviconUrl: cachedTheme?.colors.faviconUrl ?? null,
    themeConfig: cachedTheme?.colors.themeConfig ?? null,
    metadata: cachedTheme?.colors.metadata ?? null,
    name: cachedTheme?.colors.name ?? null,
    title: cachedTheme?.colors.title ?? null,
    description: cachedTheme?.colors.description ?? null,
    currency: cachedTheme?.colors.currency ?? null,
    paymentMethod,
  };

  setCookie(
    'tenant-theme',
    JSON.stringify({
      colors,
      computed: resolveTheme(colors),
    }),
    365
  );

  deleteCookie(TENANT_PAYMENT_METHOD_COOKIE);
};

export const getTenantPaymentMethodCookie = (): TenantPaymentMethod | null => {
  if (getCookie(TENANT_PAYMENT_METHOD_COOKIE) !== null) {
    deleteCookie(TENANT_PAYMENT_METHOD_COOKIE);
  }

  const cachedTheme = getThemeCookie();
  const themePaymentMethod = cachedTheme?.colors.paymentMethod;
  if (isTenantPaymentMethod(themePaymentMethod)) return themePaymentMethod;

  return null;
};

export const setTenantCurrencyCookie = (currency: string) => {
  setCookie(TENANT_CURRENCY_COOKIE, currency.toUpperCase(), 365);
};

export const getTenantCurrencyCookie = (): string | null => {
  const currency = getCookie(TENANT_CURRENCY_COOKIE);
  if (isTenantCurrency(currency)) return currency;

  const cachedTheme = getThemeCookie();
  const themeCurrency = cachedTheme?.colors.currency;
  return isTenantCurrency(themeCurrency) ? themeCurrency : null;
};
